import { Trans } from '@lingui/react/macro';
import { useSelector } from 'react-redux';

import { getPatientAge } from '@/modules/patientList/patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfo';

import { useGetDateFNSLocale } from '../../../locales/helpers';
import { PrivacyButton } from '../../../modules/patientList/PrivacyButton';
import { useLatestImagesV2ToV1 } from '../../../react-query/useLatestImagesv2';
import { parseAndFormatDate } from '../../../utils/parseDate';

interface PatientInfoProps {
  patientId: string;
}

const PrivacyFilter = () => (
  <span className="vai-text-gray-400 vai-inline vai-blur-sm">
    &#x2588;&#x2588;&#x2588;
  </span>
);

export const PatientInfo = ({ patientId }: PatientInfoProps) => {
  const privacyOn = useSelector((state: any) => state.login?.user?.privacyOn);
  const dateFNSLocale = useGetDateFNSLocale();

  // Get current patient from Redux state
  const patients = useSelector((state: any) => state.annotator?.patients) || {};
  const currentPatient = patients[patientId] || {};

  // Get latest images data which contains patient information
  const latestImages = useLatestImagesV2ToV1({
    videaPatientId: patientId,
    externalPatientId: currentPatient?.externalPatientId,
    options: {
      enabled: currentPatient?.externalPatientId
        ? currentPatient.dataSource === 'NUCLEUS'
        : true,
    },
  });

  // Get patient data from latest images or current patient
  const patientName =
    latestImages.data?.patientName || currentPatient?.name || 'Unknown Patient';
  const patientDOB = latestImages.data?.dob || currentPatient?.dob;

  const formattedDOB = parseAndFormatDate(patientDOB, undefined, dateFNSLocale);
  const age = getPatientAge(patientDOB);

  // Show loading state while fetching patient data
  if (latestImages.isLoading) {
    return (
      <div className="vai-flex vai-flex-row vai-gap-1 vai-items-center">
        <div className="vai-h-6 vai-w-32 vai-bg-gray-200 vai-rounded vai-animate-pulse" />
        <div className="vai-h-4 vai-w-24 vai-bg-gray-200 vai-rounded vai-animate-pulse" />
      </div>
    );
  }

  const displayName = !privacyOn ? (
    <span>
      {patientName.split(' ').map((name: string, index: number) => (
        <span key={index}>
          {name[0]}
          <PrivacyFilter />
          {index < patientName.split(' ').length - 1 && ' '}
        </span>
      ))}
    </span>
  ) : (
    patientName
  );

  const displayAgeAndDOB = !privacyOn ? (
    <PrivacyFilter />
  ) : (
    <>
      {age !== null && (
        <span>
          <Trans>Age {age}</Trans>
          {formattedDOB && ' | '}
        </span>
      )}
      {formattedDOB && (
        <span>
          <Trans>DOB: {formattedDOB}</Trans>
        </span>
      )}
    </>
  );

  return (
    <div className="vai-flex vai-items-center vai-gap-2 vai-whitespace-nowrap vai-font-inter">
      <span
        className="vai-text-white vai-font-semibold vai-text-base"
        data-testid="active-patient-button"
      >
        {displayName}
      </span>
      <span
        className="vai-text-white vai-text-[12px]"
        data-testid="patient-age-and-dob"
      >
        {displayAgeAndDOB}
      </span>
      <PrivacyButton
        source="IMAGE_VIEWER"
        className="vai-text-white hover:vai-text-white hover:vai-bg-white/10"
      />
    </div>
  );
};
